import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { testServerConnection } from '../utils/test-connection.js';
import { ApiService } from '../utils/api.js';
import { projectId, publicAnonKey } from '../utils/supabase/info.tsx';
import { EDGE_FUNCTIONS_BASE } from '../utils/edge';

interface DebugPanelProps {
  isVisible?: boolean;
  onToggle?: () => void;
}

export function DebugPanel({ isVisible = false, onToggle }: DebugPanelProps) {
  const [testResults, setTestResults] = useState<any>(null);
  const [isTestRunning, setIsTestRunning] = useState(false);

  const runConnectionTest = async () => {
    setIsTestRunning(true);
    try {
      const result = await testServerConnection();
      setTestResults(result);
    } catch (error) {
      setTestResults({ success: false, error: error?.message || 'Unknown error' });
    } finally {
      setIsTestRunning(false);
    }
  };

  const runHealthCheck = async () => {
    setIsTestRunning(true);
    try {
      const result = await ApiService.healthCheck();
      setTestResults({ healthCheck: result });
    } catch (error) {
      setTestResults({ healthCheck: { healthy: false, error: error?.message || 'Unknown error' } });
    } finally {
      setIsTestRunning(false);
    }
  };

  if (!isVisible) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button 
          onClick={onToggle}
          variant="outline" 
          size="sm"
          className="bg-background/80 backdrop-blur-sm"
        >
          Debug
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-96">
      <Card className="p-4 bg-background/95 backdrop-blur-sm border shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium">Debug Panel</h3>
          <Button onClick={onToggle} variant="ghost" size="sm">✕</Button>
        </div>
        
        <div className="space-y-4">
          {/* System Info */}
          <div>
            <h4 className="text-sm font-medium mb-2">System Info</h4>
            <div className="text-xs space-y-1 text-muted-foreground">
              <div>Project ID: {projectId}</div>
              <div>API Key: {publicAnonKey ? `${publicAnonKey.slice(0, 20)}...` : 'Missing'}</div>
              <div>Health: {EDGE_FUNCTIONS_BASE}/adcanvas-health</div>
              <div>Facebook: {EDGE_FUNCTIONS_BASE}/adcanvas-facebook</div>
            </div>
          </div>

          {/* Test Buttons */}
          <div className="flex gap-2">
            <Button 
              onClick={runConnectionTest} 
              disabled={isTestRunning}
              size="sm"
              variant="outline"
            >
              {isTestRunning ? 'Testing...' : 'Test Connection'}
            </Button>
            <Button 
              onClick={runHealthCheck} 
              disabled={isTestRunning}
              size="sm"
              variant="outline"
            >
              Health Check
            </Button>
          </div>

          {/* Test Results */}
          {testResults && (
            <div>
              <h4 className="text-sm font-medium mb-2">Test Results</h4>
              <div className="text-xs bg-muted p-3 rounded-md">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant={testResults.success ? 'default' : 'destructive'}>
                    {testResults.success ? 'Success' : 'Failed'}
                  </Badge>
                </div>
                <pre className="whitespace-pre-wrap overflow-auto max-h-32">
                  {JSON.stringify(testResults, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-medium mb-2">Quick Tests</h4>
            <div className="space-y-1 text-xs">
              <a
                href={`${EDGE_FUNCTIONS_BASE}/adcanvas-health/health`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline block"
              >
                → Health Endpoint
              </a>
              <a
                href={`${EDGE_FUNCTIONS_BASE}/adcanvas-health/test-db`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline block"
              >
                → Database Test
              </a>
              <a
                href={`${EDGE_FUNCTIONS_BASE}/adcanvas-facebook/creatives?limit=3`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline block"
              >
                → Creatives API
              </a>
            </div>
          </div>

          {/* Version */}
          <div className="border-t pt-4">
            <span className="text-xs text-muted-foreground">v1.0.0</span>
          </div>
        </div>
      </Card>
    </div>
  );
}